import React, { useEffect } from 'react'
import { FormattedMessage } from 'react-intl'
import { useSelector, useDispatch } from 'react-redux'

// Constants
import { GOOGLE_STORE_URL } from 'src/constants/chromeBot'
import { TAB_NAMES } from 'src/constants/navigation'

// Selectors
import {
  selectCustomerId,
  selectCustomerPhotoUri,
} from 'src/store/selectors/customerProfile'

// Actions
import { setActiveTab } from 'src/actions/navigation'
import { fetchSavedSearches } from 'src/actions/savedSearches'

// Components
import ContactBubbleSvg from 'src/svgs/ContactBubbleSvg.jsx'
import { ExternalLinkButton, Text } from 'src/Components'
import FeatureSection from 'src/Components/homeSearch/FeatureSection'

// Assets
import checkboxIcon from './assets/checkbox_icon.svg'
import chromeIcon from './assets/chrome_icon.svg'
import chromeScene from './assets/chrome_scene.png'
import magicWandIcon from './assets/magic_wand_icon.svg'

// Local Components
import SearchBar from './SearchBar'

// Styles
import styles from './Search.module.scss'

const Search = () => {
  const dispatch = useDispatch()
  const customerId = useSelector(selectCustomerId)
  const customerPhoto = useSelector(selectCustomerPhotoUri)

  useEffect(() => {
    dispatch(setActiveTab(TAB_NAMES.SEARCH))
  }, [dispatch])

  useEffect(() => {
    dispatch(fetchSavedSearches(customerId))
  }, [dispatch, customerId])

  return (
    <div className={styles.wrapper}>
      <div className={styles.contentWrapper}>
        <div className={styles.searchWrapper}>
          <div className={styles.search}>
            <Text element='h1' className={styles.title}>
              <FormattedMessage
                id='HomeSearch.search.heading'
                defaultMessage='Focus on finding your home. We’ll crunch the numbers.'
              />
            </Text>
            <SearchBar />
          </div>
        </div>
        <div className={styles.featureSections}>
          <FeatureSection
            className={styles.firstFeatureSection}
            image={
              <img
                src={magicWandIcon}
                className={styles.featureSectionImage}
                alt=''
              />
            }
            title={
              <FormattedMessage
                id='HomeSearch.search.feature.customInsights.title'
                defaultMessage='Custom insights fit for your cash flow'
              />
            }
            description={
              <FormattedMessage
                id='HomeSearch.search.feature.customInsights.description'
                defaultMessage='Based on your budget, we crunch the numbers to help you find a home that fits your financial goals.'
              />
            }
          />
          <FeatureSection
            className={styles.secondFeatureSection}
            image={
              <ContactBubbleSvg
                className={styles.featureSectionImage}
                photo={customerPhoto}
              />
            }
            title={
              <FormattedMessage
                id='HomeSearch.search.feature.experts.title'
                defaultMessage='Experts with you at every step'
              />
            }
            description={
              <FormattedMessage
                id='HomeSearch.search.feature.experts.description'
                defaultMessage='Without support, finding a new house can be overwhelming. Take comfort in knowing your team is with you at every step.'
              />
            }
          />
          <FeatureSection
            className={styles.thirdFeatureSection}
            image={
              <img
                src={checkboxIcon}
                className={styles.featureSectionImage}
                alt=''
              />
            }
            title={
              <FormattedMessage
                id='HomeSearch.search.feature.latest.title'
                defaultMessage='Get the latest homes on the market'
              />
            }
            description={
              <FormattedMessage
                id='HomeSearch.search.feature.latest.description'
                defaultMessage='In a market this hot, keeping up with new listings is critical to you finding a home fast.'
              />
            }
          />
        </div>
        <div className={styles.chromeBotWrapper}>
          <img
            className={styles.chromeBotImage}
            src={chromeScene}
            alt={
              <FormattedMessage
                id='HomeSearch.search.feature.chromeBotImage.altText'
                defaultMessage='Man and woman using Homebot on a laptop'
              />
            }
          />
          <div className={styles.chromeBotFeature}>
            <FeatureSection
              className={styles.chromeBotFeatureSection}
              image={
                <img
                  src={chromeIcon}
                  className={styles.featureSectionImage}
                  alt=''
                />
              }
              title={
                <FormattedMessage
                  id='HomeSearch.search.feature.chromeBot.title'
                  defaultMessage='Do you use Google Chrome?'
                />
              }
              description={
                <FormattedMessage
                  id='HomeSearch.search.feature.chromeBot.description'
                  defaultMessage='Take Homebot’s simple, smart financial insights with you on other search sites.'
                />
              }
            />
            <ExternalLinkButton
              className={styles.chromeBotButton}
              href={GOOGLE_STORE_URL}
              primary
            >
              <FormattedMessage
                id='HomeSearch.search.feature.chromeBot.cta'
                defaultMessage='Get free extension'
              />
            </ExternalLinkButton>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Search
